import {
  getAllowedFilterOperators,
  type TableDescriptor,
  type TableColumnDescriptor,
  type Locale,
  type TableFilterOperator,
  type TableAggregationFunction,
  type TableQueryRequest,
  type TableQueryFilter,
  type TableQuerySort,
  type TableQueryGroup,
  type TableQueryAggregation,
} from '@finance/contracts';
import type {
  TableSortState,
  TableFilterState,
  TableGroupState,
  TableAggregationState,
} from './table-state.js';

export function buildTableQuery(
  descriptor: TableDescriptor,
  state: {
    sorting: TableSortState[];
    filters: TableFilterState[];
    globalSearch: string;
    grouping: TableGroupState[];
    aggregations: TableAggregationState[];
    pagination: { page: number; pageSize: number } | { cursor: string; limit: number };
    columnVisibility: { columnId: string; visible: boolean }[];
    referenceInclusion: string[];
  },
  locale: Locale,
  timezone: string
): TableQueryRequest {
  const visibleColumns = getVisibleColumns(descriptor, state.columnVisibility);
  // List queries request visible canonical fields. Export aliases belong to the
  // export workflow and must never change the normal table projection.
  const fields = visibleColumns.map(c => c.field);
  const referenceInclusion = state.referenceInclusion;

  const sort = state.sorting
    .map(s => {
      const col = descriptor.columns.find(c => c.field === s.field || c.id === s.field);
      if (!col || !col.sortable) return null;
      // The authenticated backend descriptor owns any storage/server alias.
      return { field: col.field, direction: s.direction };
    })
    .filter(Boolean) as TableQuerySort[];

  const filters = state.filters
    .map(f => {
      const col = descriptor.columns.find(c => c.field === f.field || c.id === f.field);
      if (!col || !col.filterable) return null;
      if (!isOperatorAllowed(col, f.operator)) return null;
      return { field: col.field, operator: f.operator as TableFilterOperator, value: f.value };
    })
    .filter(Boolean) as TableQueryFilter[];

  const globalSearch = state.globalSearch
    ? {
        query: state.globalSearch,
        fields: descriptor.columns.filter(c => c.searchable).map(c => c.field),
      }
    : undefined;

  const grouping = state.grouping
    .map(g => {
      const col = descriptor.columns.find(c => c.field === g.field || c.id === g.field);
      if (!col || !col.groupable) return null;
      return { field: col.groupKey ?? col.field, direction: g.direction };
    })
    .filter(Boolean) as TableQueryGroup[];

  const aggregations = state.aggregations
    .map(a => {
      const col = descriptor.columns.find(c => c.field === a.field || c.id === a.field);
      if (!col || !col.allowedAggregations?.includes(a.function as TableAggregationFunction)) return null;
      return { field: col.aggregationKey ?? col.field, function: a.function as TableAggregationFunction };
    })
    .filter(Boolean) as TableQueryAggregation[];

  let pagination: TableQueryRequest['pagination'];
  if ('page' in state.pagination) {
    pagination = { page: state.pagination.page, pageSize: state.pagination.pageSize };
  } else {
    pagination = { cursor: state.pagination.cursor, limit: state.pagination.limit };
  }

  return {
    fields,
    globalSearch,
    filters,
    sort,
    grouping,
    aggregations,
    pagination,
    referenceInclusion,
    locale,
    timezone,
  };
}

function getVisibleColumns(
  descriptor: TableDescriptor,
  visibility: { columnId: string; visible: boolean }[]
): TableColumnDescriptor[] {
  const visibleMap = new Map(visibility.map(v => [v.columnId, v.visible]));
  return descriptor.columns.filter(c => {
    if (c.requiredVisible) return true;
    return visibleMap.get(c.id) !== false;
  });
}

function isOperatorAllowed(column: TableColumnDescriptor, operator: string): boolean {
  return getAllowedFilterOperators(column).includes(operator as TableFilterOperator);
}
